import type { ReactNode } from 'react'
import Box from '@mui/joy/Box'
import type { SxProps } from '@mui/joy/styles/types'
import { styled } from '@mui/joy/styles'

export const FLOATING_LABEL_FIELD_CLASSES = {
    focused: 'floating-label-field--focused',
    filled: 'floating-label-field--filled',
    error: 'floating-label-field--error',
    disabled: 'floating-label-field--disabled',
} as const

export const FLOATING_LABEL_FIELD_INLINE_PADDING = 12

const StyledLabel = styled('label')(({ theme }) => ({
    position: 'absolute',
    lineHeight: 1,
    top: 'calc((var(--FloatingLabelField-minHeight) - 1em) / 2)',
    fontSize: '15px',
    color: theme.vars.palette.text.tertiary,
    fontWeight: theme.vars.fontWeight.md,
    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    [`.${FLOATING_LABEL_FIELD_CLASSES.focused} &, .${FLOATING_LABEL_FIELD_CLASSES.filled} &`]: {
        top: '0.5rem',
        fontSize: '0.75rem',
    },
    [`.${FLOATING_LABEL_FIELD_CLASSES.focused} &`]: {
        color: 'var(--joy-palette-primary-500)',
    },
    [`.${FLOATING_LABEL_FIELD_CLASSES.error} &`]: {
        color: 'var(--joy-palette-danger-500)',
    },
}))

const StyledAsterisk = styled('span')({
    marginLeft: '0.2em',
})

const StyledStartDecorator = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    paddingRight: '8px',
    paddingTop: '1em',
})

const StyledFieldBody = styled('div')({
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch',
    flexGrow: 1,
    minWidth: 0,
    paddingLeft: `${FLOATING_LABEL_FIELD_INLINE_PADDING}px`,
    paddingRight: `${FLOATING_LABEL_FIELD_INLINE_PADDING}px`,
})

export function getFloatingLabelFieldRootSx(sx?: SxProps) {
    const composedSx = Array.isArray(sx) ? sx : sx ? [sx] : []

    return [
        {
            '--FloatingLabelField-minHeight': '56px',
            '--FloatingLabelField-radius': '6px',
            display: 'flex',
            alignItems: 'stretch',
            minHeight: 'var(--FloatingLabelField-minHeight)',
            borderRadius: 'var(--FloatingLabelField-radius)',
            position: 'relative',
            backgroundColor: '#fff',
            boxShadow:
                'var(--joy-shadowRing, 0 0 #000), 0px 1px 2px 0px rgba(var(--joy-shadowChannel, 21 21 21) / var(--joy-shadowOpacity, 0.08))',
            border: '1px solid',
            borderColor: 'var(--joy-palette-neutral-outlinedBorder, var(--joy-palette-neutral-300))',
            transition: 'border-color 150ms ease, background-color 150ms ease',
            [`&.${FLOATING_LABEL_FIELD_CLASSES.error}`]: {
                borderColor: 'var(--joy-palette-danger-500)',
            },
            [`&.${FLOATING_LABEL_FIELD_CLASSES.focused}::before`]: {
                boxSizing: 'border-box',
                content: '""',
                display: 'block',
                position: 'absolute',
                pointerEvents: 'none',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                borderRadius: 'inherit',
                margin: '-1px',
                boxShadow:
                    'inset 0 0 0 var(--joy-focus-thickness) var(--joy-palette-primary-500)',
            },
            [`&.${FLOATING_LABEL_FIELD_CLASSES.disabled}`]: {
                backgroundColor: 'var(--joy-palette-neutral-50)',
                color: 'var(--joy-palette-text-tertiary)',
            },
        },
        ...composedSx,
    ]
}

export default function FloatingLabelFieldShell({
    label,
    required = false,
    htmlFor,
    focused,
    filled,
    error = false,
    disabled = false,
    startDecorator,
    children,
    sx,
}: {
    label: string
    required?: boolean
    htmlFor: string
    focused: boolean
    filled: boolean
    error?: boolean
    disabled?: boolean
    startDecorator?: ReactNode
    children: ReactNode
    sx?: SxProps
}) {
    return (
        <Box
            className={[
                focused ? FLOATING_LABEL_FIELD_CLASSES.focused : '',
                filled ? FLOATING_LABEL_FIELD_CLASSES.filled : '',
                error ? FLOATING_LABEL_FIELD_CLASSES.error : '',
                disabled ? FLOATING_LABEL_FIELD_CLASSES.disabled : '',
            ]
                .filter(Boolean)
                .join(' ')}
            sx={getFloatingLabelFieldRootSx(sx)}
        >
            <StyledFieldBody>
                {startDecorator && <StyledStartDecorator>{startDecorator}</StyledStartDecorator>}
                <StyledLabel htmlFor={htmlFor}>
                    {label}
                    {required && <StyledAsterisk aria-hidden="true">*</StyledAsterisk>}
                </StyledLabel>
                {children}
            </StyledFieldBody>
        </Box>
    )
}
